import { useParams } from "react-router-dom";
import { useGetServiceAccount } from "@/queries/service-account-queries";
import { useContext, useEffect, useMemo, type FC } from "react";
import { SettingsPageContext } from "@/contexts/SettingsPageContext";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import { Text } from "@/components/podkit/typography/Text";
import { useMembers } from "@/queries/organization-queries";
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { Timestamp } from "@bufbuild/protobuf";
import { serviceAccountsUrl } from "@/utils/service-accounts";
import { formatShortDate } from "@/format/time";

export const ServiceAccountDetailsPage: FC = () => {
    const { setCustomBreadcrumbs } = useContext(SettingsPageContext);
    const { serviceAccountId } = useParams<{ serviceAccountId: string }>();
    const { data: serviceAccount, isPending, isError } = useGetServiceAccount(serviceAccountId || "");
    const { data: membersData } = useMembers();

    const creator = useMemo(() => {
        if (!serviceAccount?.creator?.id || !membersData?.members) {
            return null;
        }
        return membersData.members.find((m) => m.userId === serviceAccount.creator?.id);
    }, [serviceAccount?.creator?.id, membersData?.members]);

    useEffect(() => {
        if (!serviceAccount) {
            setCustomBreadcrumbs([{ label: "Service Accounts", to: serviceAccountsUrl() }]);
            return;
        }

        setCustomBreadcrumbs([
            { label: "Service Accounts", to: serviceAccountsUrl() },
            { label: serviceAccount.name },
        ]);

        return () => {
            setCustomBreadcrumbs(null);
        };
    }, [serviceAccount, setCustomBreadcrumbs]);

    if (isPending) {
        return <LoadingState />;
    }

    if (isError || !serviceAccount) {
        return (
            <div className="mt-6 flex flex-col items-center justify-center">
                <Text className="text-xl font-semibold text-content-primary">Service account not found</Text>
                <Text className="text-content-secondary">
                    The service account you&apos;re looking for doesn&apos;t exist or you don&apos;t have access to it.
                </Text>
            </div>
        );
    }

    const createdDate = serviceAccount.createdAt
        ? formatShortDate(new Timestamp(serviceAccount.createdAt).toDate(), true)
        : null;

    return (
        <div className="flex h-full flex-col pt-6">
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                    <ServiceAccountAvatar id={serviceAccount.id} size={32} />
                    <Text className="text-lg font-medium text-content-primary">{serviceAccount.name}</Text>
                </div>

                {serviceAccount.description && (
                    <Text className="text-base text-content-strong">{serviceAccount.description}</Text>
                )}

                {createdDate && (
                    <div className="flex items-center gap-1.5 text-sm text-content-secondary">
                        <span>Created on {createdDate}</span>
                        {creator && (
                            <>
                                <span>by</span>
                                <div className="flex items-center gap-1.5">
                                    <Avatar size={16}>
                                        {creator.avatarUrl && (
                                            <AvatarImage src={creator.avatarUrl} alt={`${creator.fullName}'s avatar`} />
                                        )}
                                        <AvatarFallback>
                                            <AvatarInitials name={creator.fullName} size={16} />
                                        </AvatarFallback>
                                    </Avatar>
                                    <span>{creator.fullName}</span>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
